/**
 * Catalog route tree, composable under any parent path.
 *
 * Shell mounts it at `/catalog/*`; standalone mounts at the repo root. The
 * `catalogRoutes(basePath)` factory prefixes sidebar `<Link>`s with the
 * mount path. Route-object paths stay relative so React Router resolves
 * them against the mount point in either mode.
 */

import { lazy, Suspense, createContext, useContext } from 'react';
import { Outlet, type RouteObject } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import { AppShell, type NavEntry } from '@widgets/app-shell';
const ModuleGuidePage = lazy(() =>
  import('@widgets/module-guide').then((m) => ({ default: m.ModuleGuidePage }))
);
import { CatalogActivityLog } from '@catalog/features/activity-log/CatalogActivityLog';
import { ErrorBoundary } from '@shared/ui';
import { ProtectedRoute } from '@shared/auth';
import {
  BoardRegular,
  BoxMultipleRegular,
  ClipboardTaskListLtrRegular,
  WrenchScrewdriverRegular,
  ListRegular,
  LinkRegular,
  TagRegular,
  FormNewRegular,
  FolderRegular,
  GridRegular,
  EyeRegular,
  SettingsRegular,
  MailRegular,
  HistoryRegular,
  ArrowSyncCircleRegular,
  DocumentTableArrowRightRegular,
  DocumentSearchRegular,
} from '@fluentui/react-icons';

const SolutionsPage = lazy(() => import('@catalog/pages/solutions').then(m => ({ default: m.SolutionsPage })));
const ManagedListsPage = lazy(() => import('@catalog/pages/managed-lists').then(m => ({ default: m.ManagedListsPage })));
const GroupInfoPage = lazy(() => import('@catalog/pages/group-info').then(m => ({ default: m.GroupInfoPage })));
const PlanDesignPage = lazy(() => import('@catalog/pages/plan-design').then(m => ({ default: m.PlanDesignPage })));
const MasterDataPage = lazy(() => import('@catalog/pages/master-data').then(m => ({ default: m.MasterDataPage })));
const PartnerIdsPage = lazy(() => import('@catalog/pages/partner-ids').then(m => ({ default: m.PartnerIdsPage })));
const LinkSquaresPage = lazy(() => import('@catalog/pages/linksquares').then(m => ({ default: m.LinkSquaresPage })));
const AdminConfigPage = lazy(() => import('@catalog/pages/admin-config').then(m => ({ default: m.AdminConfigPage })));
const DashboardPage = lazy(() => import('@catalog/pages/dashboard').then(m => ({ default: m.DashboardPage })));
const SolutionOverviewPage = lazy(() => import('@catalog/pages/solution-overview').then(m => ({ default: m.SolutionOverviewPage })));
const CatalogReportPage = lazy(() => import('@catalog/pages/catalog-report').then(m => ({ default: m.CatalogReportPage })));
const DemoPreviewPage = lazy(() => import('@catalog/pages/demo-preview').then(m => ({ default: m.DemoPreviewPage })));
const ImplementationPage = lazy(() => import('@catalog/pages/implementation').then(m => ({ default: m.ImplementationPage })));
const TemplateEditorPage = lazy(() => import('@catalog/pages/implementation').then(m => ({ default: m.TemplateEditorPage })));
const SectionEditorPage = lazy(() => import('@catalog/pages/implementation').then(m => ({ default: m.SectionEditorPage })));
const ImpGuidePage = lazy(() => import('@catalog/pages/implementation').then(m => ({ default: m.ImpGuidePage })));
const EmailTemplatesPage = lazy(() => import('@catalog/pages/email-templates').then(m => ({ default: m.EmailTemplatesPage })));
const FormsPage = lazy(() => import('@catalog/pages/forms').then(m => ({ default: m.FormsPage })));
const FormEditorPage = lazy(() => import('@catalog/pages/forms').then(m => ({ default: m.FormEditorPage })));
const FormSectionEditorPage = lazy(() => import('@catalog/pages/forms').then(m => ({ default: m.FormSectionEditorPage })));
const ServiceEditorPage = lazy(() => import('@catalog/pages/service-editor').then(m => ({ default: m.ServiceEditorPage })));
const DocStructuresPage = lazy(() => import('@catalog/pages/doc-structures').then(m => ({ default: m.DocStructuresPage })));
const PropagationPage = lazy(() => import('@catalog/pages/propagation').then(m => ({ default: m.PropagationPage })));
const VendorIntakePage = lazy(() => import('@catalog/pages/plan-design').then(m => ({ default: m.VendorIntakePage })));
const BulkUpdatePage = lazy(() => import('@catalog/pages/bulk-update').then(m => ({ default: m.BulkUpdatePage })));

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Spinner style={{ padding: '2rem' }} label="Loading..." />}>{children}</Suspense>;
}

const CatalogBasePathContext = createContext<string>('');

function buildNavItems(basePath: string): NavEntry[] {
  const p = (suffix: string) => `${basePath}${suffix}` || '/';
  return [
    // Top-level, unheaded (mirrors the Ops nav): the landing surfaces.
    { path: p('/'), label: 'Dashboard', icon: BoardRegular },
    // What a group is made of: the template data Ops consumes.
    { kind: 'section', label: 'Group Data' },
    { path: p('/solutions'), label: 'Solutions', icon: BoxMultipleRegular },
    { path: p('/group-info'), label: 'Group Info', icon: ClipboardTaskListLtrRegular },
    { path: p('/propagation'), label: 'Propagation', icon: ArrowSyncCircleRegular },
    { path: p('/bulk-update'), label: 'Bulk Update', icon: DocumentTableArrowRightRegular },
    { path: p('/managed-lists'), label: 'Managed Lists', icon: ListRegular },
    { path: p('/partner-ids'), label: 'Partner IDs', icon: LinkRegular },
    { path: p('/demo-preview'), label: 'Demo Preview', icon: EyeRegular },
    // Groups moving into or through the book.
    { kind: 'section', label: 'Transitions' },
    { path: p('/implementation'), label: 'Implementation', icon: WrenchScrewdriverRegular },
    { path: p('/forms'), label: 'Forms', icon: FormNewRegular },
    { path: p('/plan-design'), label: 'Plan Design', icon: GridRegular },
    { path: p('/plan-design/intake'), label: 'Vendor Intake', icon: DocumentSearchRegular },
    // Authored artifacts and integration reference data.
    { kind: 'section', label: 'Templates and Configurations' },
    { path: p('/doc-structures'), label: 'Document Structures', icon: FolderRegular },
    { path: p('/email-templates'), label: 'Outlook Templates', icon: MailRegular },
    { path: p('/linksquares'), label: 'LinkSquares Tags', icon: TagRegular },
    { kind: 'section', label: 'Admin' },
    { path: p('/admin-config'), label: 'Configuration', icon: SettingsRegular },
    { path: p('/activity-log'), label: 'Activity Log', icon: HistoryRegular },
  ];
}

export function CatalogRootLayout() {
  const basePath = useContext(CatalogBasePathContext);
  const navItems = buildNavItems(basePath);
  return (
    <ProtectedRoute appRolePrefix="Catalog">
      <ErrorBoundary>
        <AppShell
          appId="catalog"
          headerColor="#3F4C5C"
          navItems={navItems}
        >
          <Outlet />
        </AppShell>
      </ErrorBoundary>
    </ProtectedRoute>
  );
}

/**
 * Returns the Catalog route tree, with sidebar links prefixed for the mount
 * path. Standalone: `catalogRoutes()` at repo root. Shell: `catalogRoutes('/catalog')`.
 */
export function catalogRoutes(basePath: string = ''): RouteObject[] {
  return [
    {
      element: (
        <CatalogBasePathContext.Provider value={basePath}>
          <CatalogRootLayout />
        </CatalogBasePathContext.Provider>
      ),
      children: [
        { index: true, element: <LazyPage><DashboardPage /></LazyPage> },
        { path: 'solutions', element: <LazyPage><SolutionsPage /></LazyPage> },
        { path: 'solutions/:solutionId/overview', element: <LazyPage><SolutionOverviewPage /></LazyPage> },
        { path: 'solutions/report', element: <LazyPage><CatalogReportPage /></LazyPage> },
        { path: 'services/:serviceId', element: <LazyPage><ServiceEditorPage /></LazyPage> },
        { path: 'managed-lists', element: <LazyPage><ManagedListsPage /></LazyPage> },
        { path: 'group-info', element: <LazyPage><GroupInfoPage /></LazyPage> },
        { path: 'propagation', element: <LazyPage><PropagationPage /></LazyPage> },
        { path: 'bulk-update', element: <LazyPage><BulkUpdatePage /></LazyPage> },
        { path: 'plan-design', element: <LazyPage><PlanDesignPage /></LazyPage> },
        { path: 'plan-design/intake', element: <LazyPage><VendorIntakePage /></LazyPage> },
        { path: 'master-data', element: <LazyPage><MasterDataPage /></LazyPage> },
        { path: 'partner-ids', element: <LazyPage><PartnerIdsPage /></LazyPage> },
        { path: 'linksquares', element: <LazyPage><LinkSquaresPage /></LazyPage> },
        { path: 'doc-structures', element: <LazyPage><DocStructuresPage /></LazyPage> },
        { path: 'implementation', element: <LazyPage><ImplementationPage /></LazyPage> },
        { path: 'implementation/guide', element: <LazyPage><ImpGuidePage /></LazyPage> },
        { path: 'implementation/section/:sectionId', element: <LazyPage><SectionEditorPage /></LazyPage> },
        { path: 'implementation/:templateId', element: <LazyPage><TemplateEditorPage /></LazyPage> },
        { path: 'forms', element: <LazyPage><FormsPage /></LazyPage> },
        { path: 'forms/sections/:sectionId', element: <LazyPage><FormSectionEditorPage /></LazyPage> },
        { path: 'forms/:formId', element: <LazyPage><FormEditorPage /></LazyPage> },
        { path: 'demo-preview', element: <LazyPage><DemoPreviewPage /></LazyPage> },
        { path: 'admin-config', element: <LazyPage><AdminConfigPage /></LazyPage> },
        { path: 'activity-log', element: <LazyPage><CatalogActivityLog /></LazyPage> },
        { path: 'email-templates', element: <LazyPage><EmailTemplatesPage /></LazyPage> },
        // Guide retired into Academy Know (module filter preserved).
        // The module's own documentation, read in the module's own chrome. This
        // replaced a redirect to /academy/know, which sent people out of the
        // module to read about it (Sasha, 2026-08-16).
        { path: 'guide', element: <LazyPage><ModuleGuidePage module="catalog" basePath={basePath} /></LazyPage> },
        { path: 'guide/:slug', element: <LazyPage><ModuleGuidePage module="catalog" basePath={basePath} /></LazyPage> },
      ],
    },
  ];
}

